// Hook: useCurrentTime
//
// Returns a `Date` that updates on the supplied interval. This is the single
// ticking clock used by `VerticalJourneyTimeline` to drive the Event_State
// classification pass (see design.md §Re-Render Cadence). The default cadence
// of 30 seconds matches the existing rhythm used elsewhere in the trip UI.
//
// The hook is intentionally minimal:
//   • On mount it snapshots `new Date()` and schedules a `setInterval`.
//   • Every `intervalMs` milliseconds it swaps in a fresh `new Date()`.
//   • On unmount (or when `intervalMs` changes) the interval is cleared.
//
// Consumers treat the returned value as immutable — each tick produces a new
// `Date` reference so `useMemo` / `useEffect` dependencies keyed on `now`
// invalidate correctly.
//
// Requirement: 7.4 — timeline re-classifies events at a 30-second cadence.

import { useEffect, useState } from 'react';

/**
 * @param {number} [intervalMs=30000] - tick cadence in milliseconds.
 * @returns {Date} the current clock value, refreshed every `intervalMs`.
 */
export function useCurrentTime(intervalMs = 30000) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return now;
}

export default useCurrentTime;
